import { useEffect, useRef, useState } from 'react'

import { useUserInfo } from 'hooks/useUserInfo'
import { SurveyType } from 'services/fake-door/types'
import { useGetSurveyResult, useSubmitSurveyResult } from 'services/fake-door/useFakeDoor'
import { FeedbackPage } from 'components/FeedbackPage'
import { FeedbackPageFlow, FeedbackPageType } from 'components/FeedbackPage/types'
import cardImage from 'assets/images/international-card.png'

import { Image, Text } from '@interco/inter-toranja'
import { interWbNavigate } from '@interco/inter-webview-bridge'
import { FlexColumn } from '@interco/inter-ui/components/Flex'
import { Skeleton } from '@interco/inter-ui/components/Skeleton'
import { TextSize } from '@interco/inter-toranja/dist/components/Atoms/Text/types'

import {
  trackBannerClick,
  trackContinue,
  trackFeedbackButtonClick,
  trackFeedbackLoad,
  trackGoBackHome,
  trackHeaderBack,
  trackSurveyStart,
} from './analytics'
import {
  FEEDBACK_CONTENT,
  FeedbackStepId,
  getAnalyticsStep,
  QUESTION_STEPS,
  QuestionStepId,
  resolveNextStep,
  SurveyStep,
} from './data'
import { QuestionStep } from './QuestionStep'

type SurveyAnswers = Partial<Record<QuestionStepId, string[]>>

export const BankNoteWorldCupSearch = () => {
  const { account: accountNumber } = useUserInfo()
  const { data: surveyData, isLoading: isSurveyLoading } = useGetSurveyResult(
    SurveyType.WORLD_CUP,
    accountNumber ?? '',
  )
  const submitSurveyMutation = useSubmitSurveyResult()
  const hasPostedCompletionRef = useRef(false)

  const [step, setStep] = useState<SurveyStep>('q1')
  const [history, setHistory] = useState<SurveyStep[]>([])
  const [answers, setAnswers] = useState<SurveyAnswers>({})

  const isInitialLoading = isSurveyLoading
  const alreadyCompleted = surveyData?.hasCompletedSurvey === true

  const handleBannerClick = (contentName: string) => {
    trackBannerClick({ step: getAnalyticsStep(step), contentName })
    interWbNavigate.openDeepLink('bancointer://globalAccount/cartao')
  }

  useEffect(() => {
    if (isSurveyLoading) {
      return
    }
    if (surveyData?.hasCompletedSurvey) {
      trackFeedbackLoad(FEEDBACK_CONTENT['feedback-success'])
    }
  }, [isSurveyLoading, surveyData?.hasCompletedSurvey])

  useEffect(() => {
    if (isSurveyLoading) {
      return
    }
    if (surveyData?.hasCompletedSurvey) {
      return
    }

    const question = QUESTION_STEPS[step as QuestionStepId]

    if (question) {
      if (question.id === 'q1') {
        trackSurveyStart(question)
      }
      return
    }

    trackFeedbackLoad(FEEDBACK_CONTENT[step as FeedbackStepId])
  }, [step, isSurveyLoading, surveyData?.hasCompletedSurvey])

  useEffect(() => {
    if (step in QUESTION_STEPS) {
      return
    }
    if (surveyData?.hasCompletedSurvey) {
      return
    }
    if (hasPostedCompletionRef.current) {
      return
    }

    hasPostedCompletionRef.current = true
    submitSurveyMutation.mutate({
      surveyType: SurveyType.WORLD_CUP,
      accountNumber,
    })
  }, [step, surveyData?.hasCompletedSurvey, accountNumber, submitSurveyMutation])

  const handleSelectOption = (optionId: string) => {
    const question = QUESTION_STEPS[step as QuestionStepId]

    if (question.type === 'checkbox') {
      const option = question.options.find((o) => o.id === optionId)
      const currentIds = answers[step as QuestionStepId] ?? []

      let nextIds: string[]

      if (option?.exclusive) {
        nextIds = currentIds.includes(optionId) ? [] : [optionId]
      } else {
        const withoutExclusives = currentIds.filter(
          (id) => !question.options.find((o) => o.id === id)?.exclusive,
        )
        nextIds = withoutExclusives.includes(optionId)
          ? withoutExclusives.filter((id) => id !== optionId)
          : [...withoutExclusives, optionId]
      }

      setAnswers((previousAnswers) => ({
        ...previousAnswers,
        [step]: nextIds,
      }))
      return
    }

    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [step]: [optionId],
    }))
  }

  const handleHeaderBack = () => {
    trackHeaderBack({ step: getAnalyticsStep(step) })

    if (history.length === 0) {
      interWbNavigate.requestGoBack()
      return
    }

    const previousStep = history[history.length - 1]
    setHistory((h) => h.slice(0, -1))
    setStep(previousStep)
  }

  const handleContinue = () => {
    const question = QUESTION_STEPS[step as QuestionStepId]
    if (!question) {
      return
    }

    const selectedAnswerIds = answers[step as QuestionStepId] ?? []

    if (!selectedAnswerIds.filter(Boolean).length) {
      return
    }

    trackContinue(question, selectedAnswerIds)

    const nextStep = resolveNextStep(question, selectedAnswerIds)
    setHistory((h) => [...h, step])
    setStep(nextStep)
  }

  if (isInitialLoading) {
    return (
      <FlexColumn
        className="p-6"
        height="100vh"
        justifyContent="space-between"
        alignItems="center"
        gap="16px"
      >
        <div className="w-full flex flex-col gap-16 justify-center items-center">
          <Skeleton width="86%" height="30px" />
          <Skeleton width="80%" height="70px" />
          <div className="w-full flex flex-col gap-4 justify-center items-center">
            <Skeleton width="70%" height="40px" />
            <Skeleton width="70%" height="40px" />
            <Skeleton width="70%" height="40px" />
            <Skeleton width="70%" height="40px" />
          </div>
        </div>
        <Skeleton width="84%" height="40px" />
      </FlexColumn>
    )
  }

  if (alreadyCompleted) {
    return (
      <FeedbackPage
        feedbackType={FeedbackPageType.SUCCESS}
        flow={FeedbackPageFlow.BANK_NOTE_WORLD_CUP_SEARCH}
        hasGoBackHome
        onBeforeButtonClick={() =>
          trackFeedbackButtonClick({ step: FEEDBACK_CONTENT['feedback-success'].stepNumber })
        }
        onBeforeGoBackHome={() =>
          trackGoBackHome({ step: FEEDBACK_CONTENT['feedback-success'].stepNumber })
        }
      >
        <div className="flex flex-col p-6 gap-4 justify-center items-center text-center">
          <Text textSize={TextSize.Large} textType="body">
            Você já respondeu essa pesquisa. Contamos com você em uma próxima!
          </Text>
        </div>
      </FeedbackPage>
    )
  }

  const questionStep = QUESTION_STEPS[step as QuestionStepId]
  const feedback = FEEDBACK_CONTENT[step as FeedbackStepId]
  const selectedIds = answers[step as QuestionStepId] ?? []

  if (questionStep) {
    return (
      <QuestionStep
        question={questionStep}
        selectedIds={selectedIds}
        onSelectOption={handleSelectOption}
        onBack={handleHeaderBack}
        onContinue={handleContinue}
      />
    )
  }

  return (
    <FeedbackPage
      feedbackType={FeedbackPageType.SUCCESS}
      flow={FeedbackPageFlow.BANK_NOTE_WORLD_CUP_SEARCH}
      hasGoBackHome
      onBeforeGoBackHome={() => trackGoBackHome({ step: feedback.stepNumber })}
      onBeforeButtonClick={() => trackFeedbackButtonClick({ step: feedback.stepNumber })}
    >
      <div className="flex flex-col p-6 gap-4 justify-center items-center text-center">
        <Text textSize={TextSize.Large} textType="body">
          {feedback.description}
        </Text>
        <div
          onClick={() => handleBannerClick('Cartão Global Inter')}
          role="button"
          tabIndex={0}
          onKeyDown={() => handleBannerClick('Cartão Global Inter')}
        >
          <Image
            contentDescription="Cartão Global Inter"
            src={{
              local: cardImage,
            }}
          />
        </div>
      </div>
    </FeedbackPage>
  )
}
